import { useState, useCallback } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import type { DealOfferingDetail, LoanOption, InvestorOption } from '@/types/deal_offering'

interface Props {
  offering?: DealOfferingDetail
  loan_id: number | null
  loan_label: string | null
  prefill_amount: number | null
  loans: LoanOption[]
  investors: InvestorOption[]
  selected_investor_ids?: number[]
}

export default function DealOfferingsNew({
  offering,
  loan_id,
  loan_label,
  prefill_amount,
  loans,
  investors,
  selected_investor_ids = [],
}: Props) {
  const { errors } = usePage().props as unknown as { errors?: Record<string, string[]> }
  const isEdit = !!offering

  const [selectedLoanId, setSelectedLoanId] = useState<number | ''>(offering?.loan_id ?? loan_id ?? '')
  const [totalAmount, setTotalAmount] = useState(offering?.total_amount?.toString() ?? prefill_amount?.toString() ?? '')
  const [minCommitment, setMinCommitment] = useState(offering?.min_commitment?.toString() ?? '')
  const [dealMemo, setDealMemo] = useState(offering?.deal_memo ?? '')
  const [selectedInvestorIds, setSelectedInvestorIds] = useState<number[]>(selected_investor_ids)
  const [aiLoading, setAiLoading] = useState(false)

  const selectedLoan = loans.find((l) => l.id === selectedLoanId)

  function handleLoanChange(id: number) {
    setSelectedLoanId(id)
    const loan = loans.find((l) => l.id === id)
    if (loan && !totalAmount) {
      setTotalAmount(loan.principal.toString())
    }
  }

  function toggleInvestor(id: number) {
    setSelectedInvestorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const generateMemo = useCallback(async () => {
    if (!selectedLoanId) return
    setAiLoading(true)
    try {
      const res = await fetch('/api/ai/deal_memo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '',
        },
        body: JSON.stringify({ loan_id: selectedLoanId }),
      })
      const data = await res.json()
      if (data.success && data.memo) {
        setDealMemo(data.memo)
      }
    } catch {
      // silent fail — user can write manually
    } finally {
      setAiLoading(false)
    }
  }, [selectedLoanId])

  function submit(publish: boolean) {
    const data = {
      deal_offering: {
        loan_id: selectedLoanId,
        total_amount: totalAmount,
        min_commitment: minCommitment || null,
        deal_memo: dealMemo || null,
      },
      investor_ids: selectedInvestorIds,
      ...(publish ? { publish: 'true' } : {}),
    }

    if (isEdit) {
      router.put(`/deal_offerings/${offering!.id}`, data)
    } else {
      router.post('/deal_offerings', data)
    }
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Link href="/deal_offerings" className="text-sm text-gray-500 hover:text-gray-700">
            &larr; Back to Offerings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">
            {isEdit ? 'Edit Draft Offering' : 'New Deal Offering'}
          </h1>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
          {/* Loan Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Loan</label>
            {loan_id && loan_label ? (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                {loan_label}
                <input type="hidden" value={loan_id} />
              </div>
            ) : (
              <select
                value={selectedLoanId}
                onChange={(e) => handleLoanChange(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Select a loan...</option>
                {loans.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label} — ${l.principal.toLocaleString()}
                  </option>
                ))}
              </select>
            )}
            {errors?.loan_id && <p className="text-xs text-red-600 mt-1">{errors.loan_id[0]}</p>}
          </div>

          {/* Total Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Offering Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                className="w-full rounded-lg border border-gray-200 pl-7 pr-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
            {errors?.total_amount && <p className="text-xs text-red-600 mt-1">{errors.total_amount[0]}</p>}
          </div>

          {/* Min Commitment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Commitment <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={minCommitment}
                onChange={(e) => setMinCommitment(e.target.value)}
                className="w-full rounded-lg border border-gray-200 pl-7 pr-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          {/* Deal Memo */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Deal Memo</label>
              <button
                type="button"
                onClick={generateMemo}
                disabled={aiLoading || !selectedLoanId}
                className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {aiLoading ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
                    </svg>
                    Generate with AI
                  </>
                )}
              </button>
            </div>
            <textarea
              value={dealMemo}
              onChange={(e) => setDealMemo(e.target.value)}
              rows={8}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Describe the deal opportunity for investors..."
            />
          </div>

          {/* Investor Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Invite Investors</label>
            {investors.length === 0 ? (
              <p className="text-sm text-gray-400">
                No investors yet.{' '}
                <Link href="/investors/new" className="text-emerald-600 hover:text-emerald-700 font-medium">
                  Add an investor
                </Link>
              </p>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-60 overflow-y-auto">
                {investors.map((inv) => (
                  <label
                    key={inv.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedInvestorIds.includes(inv.id)}
                      onChange={() => toggleInvestor(inv.id)}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{inv.name}</p>
                      {inv.email && <p className="text-xs text-gray-400 truncate">{inv.email}</p>}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => submit(true)}
              disabled={!selectedLoanId || !totalAmount || selectedInvestorIds.length === 0}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Publish & Send Invitations
            </button>
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={!selectedLoanId || !totalAmount}
              className="px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Draft
            </button>
            <Link href="/deal_offerings" className="text-sm text-gray-500 hover:text-gray-700 ml-auto">
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
